/**
 * Construit le contexte de page envoyé à Laika (ce que le pro voit).
 * N1 côté serveur : pathname + label seulement.
 * N2 : + entity type/id + view_hint / snapshot léger.
 */

import { readPlanningViewKind } from './planningViewKind';

export type LaikaPageEntityType =
  | 'client'
  | 'animal'
  | 'service'
  | 'invoice'
  | 'quote'
  | 'booking'
  | 'conversation'
  | 'association'
  | 'member'
  | 'forfait'
  | 'stay';

export type LaikaPageId =
  | 'home'
  | 'planning'
  | 'unavailabilities'
  | 'reservations'
  | 'services'
  | 'clients'
  | 'client_detail'
  | 'animal_detail'
  | 'billing'
  | 'invoice_detail'
  | 'quotes'
  | 'stats'
  | 'messages'
  | 'settings'
  | 'forfaits'
  | 'team'
  | 'boarding'
  | 'availability'
  | 'booking_detail'
  | 'session_detail'
  | 'notes'
  | 'subscription'
  | 'ambassador'
  | 'accounting'
  | 'marketing'
  | 'association_dashboard'
  | 'association_planning'
  | 'association_members'
  | 'association_member'
  | 'association_clients'
  | 'association_events'
  | 'association_messaging'
  | 'association_cgv'
  | 'association_settings'
  | 'profile'
  | 'startup'
  | 'partners'
  | 'sms'
  | 'notifications'
  | 'stripe_setup'
  | 'sub_success'
  | 'sub_cancel'
  | 'design_system'
  | 'messaging_gate'
  | 'laika'
  | 'other';

export interface LaikaPageContext {
  pathname: string;
  page: LaikaPageId;
  label: string;
  entity?: { type: LaikaPageEntityType; id: string };
  view_hint?: string;
  /** Résumé non-PII de la vue (ex. nb RDV visibles) */
  visible_summary?: string;
}

const PAGE_LABELS: Record<LaikaPageId, string> = {
  home: 'Accueil',
  planning: 'Planning',
  unavailabilities: 'Indisponibilités',
  reservations: 'Réservations',
  services: 'Services',
  clients: 'Clients',
  client_detail: 'Fiche client',
  animal_detail: 'Fiche animal',
  billing: 'Facturation',
  invoice_detail: 'Détail facture',
  quotes: 'Devis',
  stats: 'Statistiques',
  messages: 'Messagerie',
  settings: 'Paramètres',
  forfaits: 'Forfaits',
  team: 'Équipe',
  boarding: 'Pension / boarding',
  availability: 'Disponibilités service',
  booking_detail: 'Détail réservation',
  session_detail: 'Détail séance',
  notes: 'Notes',
  subscription: 'Abonnement',
  ambassador: 'Ambassadeur',
  accounting: 'Comptabilité',
  marketing: 'Communication',
  association_dashboard: 'Associations',
  association_planning: 'Planning association',
  association_members: 'Membres association',
  association_member: 'Fiche membre association',
  association_clients: 'Clients association',
  association_events: 'Événements association',
  association_messaging: 'Messagerie association',
  association_cgv: 'CGV association',
  association_settings: 'Paramètres association',
  profile: 'Profil',
  startup: 'Démarrage',
  partners: 'Partenaires',
  sms: 'SMS',
  notifications: 'Notifications',
  stripe_setup: 'Stripe setup',
  sub_success: 'Abonnement réussi',
  sub_cancel: 'Abonnement annulé',
  design_system: 'Design system',
  messaging_gate: 'Messagerie premium',
  laika: 'Page Laika',
  other: 'Autre page',
};

const LAIKA_SNAPSHOT_KEY = 'laika:page_snapshot';

export type LaikaPageSnapshot = {
  page?: string;
  summary?: string;
  updated_at?: number;
};

/** Publié par les pages métier (ex. planning) — résumé sans PII. */
export function publishLaikaPageSnapshot(snapshot: LaikaPageSnapshot): void {
  try {
    const payload = {
      page: snapshot.page,
      summary: (snapshot.summary || '').slice(0, 280),
      updated_at: Date.now(),
    };
    sessionStorage.setItem(LAIKA_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearLaikaPageSnapshot(): void {
  try {
    sessionStorage.removeItem(LAIKA_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

export function readLaikaPageSnapshot(): LaikaPageSnapshot | null {
  try {
    const raw = sessionStorage.getItem(LAIKA_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LaikaPageSnapshot;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.updated_at && Date.now() - parsed.updated_at > 5 * 60 * 1000) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function matchAppPath(pathname: string): {
  page: LaikaPageId;
  entity?: { type: LaikaPageEntityType; id: string };
  view_hint?: string;
} {
  const path = pathname.replace(/\/+$/, '') || '/';
  const app = path.startsWith('/app') ? path.slice(4) || '/' : path;

  const patterns: Array<{
    re: RegExp;
    page: LaikaPageId;
    entityType?: LaikaPageEntityType;
    /** Capture group index for entity id (default 1). Assoc slug often group 1. */
    entityGroup?: number;
    hint?: string;
  }> = [
    { re: /^\/?$/, page: 'home', hint: 'tableau de bord accueil' },
    { re: /^\/demarrage/, page: 'startup', hint: 'onboarding / démarrage' },
    { re: /^\/profil-v2|^\/profil/, page: 'profile', hint: 'profil professionnel' },
    { re: /^\/partenaires/, page: 'partners', hint: 'partenaires in-app' },
    { re: /^\/sms/, page: 'sms', hint: 'redirige vers Communication → SMS' },
    { re: /^\/notifications/, page: 'notifications', hint: 'historique notifications' },
    { re: /^\/police-book/, page: 'boarding', hint: 'police book → pension' },
    // Associations (avant les routes génériques)
    { re: /^\/associations$/, page: 'association_dashboard', hint: 'liste associations' },
    {
      re: /^\/association\/([^/]+)\/planning/,
      page: 'association_planning',
      entityType: 'association',
      hint: 'planning collectif',
    },
    {
      re: /^\/association\/([^/]+)\/member\/([^/]+)/,
      page: 'association_member',
      entityType: 'member',
      entityGroup: 2,
      hint: 'fiche membre',
    },
    {
      re: /^\/association\/([^/]+)\/members/,
      page: 'association_members',
      entityType: 'association',
      hint: 'membres',
    },
    {
      re: /^\/association\/([^/]+)\/clients/,
      page: 'association_clients',
      entityType: 'association',
      hint: 'clients association',
    },
    {
      re: /^\/association\/([^/]+)\/events/,
      page: 'association_events',
      entityType: 'association',
      hint: 'événements',
    },
    {
      re: /^\/association\/([^/]+)\/messaging/,
      page: 'association_messaging',
      entityType: 'association',
      hint: 'messagerie association',
    },
    {
      re: /^\/association\/([^/]+)\/cgv/,
      page: 'association_cgv',
      entityType: 'association',
      hint: 'CGV association',
    },
    {
      re: /^\/association\/([^/]+)\/settings/,
      page: 'association_settings',
      entityType: 'association',
      hint: 'paramètres association',
    },
    {
      re: /^\/association\/([^/]+)/,
      page: 'association_dashboard',
      entityType: 'association',
      hint: 'espace association',
    },
    { re: /^\/planning\/unavailabilities$/, page: 'unavailabilities' },
    { re: /^\/planning/, page: 'planning', hint: 'vue planning / agenda' },
    { re: /^\/reservations/, page: 'reservations', hint: 'liste réservations / RDV' },
    { re: /^\/services/, page: 'services', hint: 'catalogue prestations' },
    { re: /^\/clients\/segments/, page: 'clients', hint: 'segments / filtres clients' },
    { re: /^\/clients\/([^/]+)$/, page: 'client_detail', entityType: 'client', hint: 'fiche client' },
    { re: /^\/clients/, page: 'clients' },
    { re: /^\/clients-chiens/, page: 'clients' },
    { re: /^\/chiens\/([^/]+)$/, page: 'animal_detail', entityType: 'animal' },
    { re: /^\/chiens$/, page: 'clients' },
    {
      re: /^\/animal\/([^/]+)\/follow-up/,
      page: 'animal_detail',
      entityType: 'animal',
      hint: 'suivi animal / follow-up',
    },
    { re: /^\/animal\/([^/]+)/, page: 'animal_detail', entityType: 'animal' },
    { re: /^\/proprio\/([^/]+)$/, page: 'client_detail', entityType: 'client' },
    { re: /^\/pro\/([^/]+)$/, page: 'client_detail', entityType: 'client' },
    { re: /^\/billing\/invoice\/([^/]+)$/, page: 'invoice_detail', entityType: 'invoice', hint: 'détail facture / devis' },
    { re: /^\/billing\/invoice$/, page: 'invoice_detail', hint: 'facture (brouillon / aperçu)' },
    { re: /^\/billing/, page: 'billing', hint: 'facturation · factures / devis' },
    {
      re: /^\/devis\/([^/]+)$/,
      page: 'quotes',
      entityType: 'quote',
      hint: 'détail devis',
    },
    { re: /^\/devis/, page: 'quotes', hint: 'liste devis' },
    {
      re: /^\/quotes\/([^/]+)$/,
      page: 'quotes',
      entityType: 'quote',
      hint: 'détail devis',
    },
    { re: /^\/quotes/, page: 'quotes', hint: 'liste devis' },
    { re: /^\/stats/, page: 'stats', hint: 'dashboard ProStatV2 · CA / période' },
    {
      re: /^\/messages\/([^/]+)$/,
      page: 'messages',
      entityType: 'conversation',
      hint: 'fil conversation',
    },
    { re: /^\/messages$/, page: 'messages', hint: 'boîte messagerie' },
    {
      re: /^\/forfaits\/([^/]+)$/,
      page: 'forfaits',
      entityType: 'forfait',
      hint: 'détail forfait / pack',
    },
    { re: /^\/forfaits/, page: 'forfaits', hint: 'packs et crédits' },
    { re: /^\/equipe/, page: 'team', hint: 'équipe / collaborateurs' },
    {
      re: /^\/stay\/([^/]+)/,
      page: 'boarding',
      entityType: 'stay',
      hint: 'détail séjour pension',
    },
    { re: /^\/boarding-sub-success/, page: 'sub_success', hint: 'abonnement boarding OK' },
    {
      re: /^\/pension|^\/hebergement|^\/boarding/,
      page: 'boarding',
      hint: 'module pension / hébergement',
    },
    {
      re: /^\/service\/([^/]+)\/availability/,
      page: 'availability',
      entityType: 'service',
      hint: 'disponibilités service · créneaux bookables · ouverture',
    },
    { re: /^\/booking\/([^/]+)/, page: 'booking_detail', entityType: 'booking', hint: 'détail RDV · actions séance' },
    { re: /^\/session\/([^/]+)/, page: 'session_detail', entityType: 'booking', hint: 'séance groupe' },
    { re: /^\/notes/, page: 'notes', hint: 'notes de séance / compte-rendus' },
    { re: /^\/subscription/, page: 'subscription', hint: 'abonnement / offre Rintintin' },
    { re: /^\/ambassadeur/, page: 'ambassador', hint: 'programme ambassadeur / code promo' },
    { re: /^\/messaging/, page: 'messages', hint: 'messagerie' },
    { re: /^\/comptabilite|^\/finance/, page: 'accounting', hint: 'dépenses' },
    {
      re: /^\/marketing/,
      page: 'marketing',
      hint: 'hub Communication · Premium requis (upsell sinon)',
    },
    { re: /^\/settings/, page: 'settings', hint: 'paramètres compte' },
    { re: /^\/stripe-setup-complete/, page: 'stripe_setup', hint: 'Stripe connecté' },
    { re: /^\/sub-success/, page: 'sub_success', hint: 'abonnement OK' },
    { re: /^\/sub-cancel/, page: 'sub_cancel', hint: 'abonnement annulé / retour' },
    { re: /^\/design-system/, page: 'design_system', hint: 'lab UI interne' },
    { re: /^\/no-premium-messaging/, page: 'messaging_gate', hint: 'messagerie premium requise' },
    { re: /^\/laika/, page: 'laika', hint: 'Laika / accès données' },
  ];

  for (const p of patterns) {
    const m = app.match(p.re);
    if (!m) continue;
    const group = p.entityGroup ?? 1;
    const entity =
      p.entityType && m[group]
        ? { type: p.entityType, id: decodeURIComponent(m[group]) }
        : undefined;
    const hintParts = [p.hint];
    if (p.entityType === 'association' && m[1] && p.page !== 'association_member') {
      hintParts.push(`asso ${decodeURIComponent(m[1])}`);
    }
    if (p.page === 'association_member' && m[1]) {
      hintParts.push(`asso ${decodeURIComponent(m[1])}`);
    }
    return {
      page: p.page,
      entity,
      view_hint: hintParts.filter(Boolean).join(' · ') || undefined,
    };
  }

  return { page: 'other' };
}

export function buildLaikaPageContext(
  pathname: string,
  search?: string,
  hash?: string,
): LaikaPageContext {
  const matched = matchAppPath(pathname);
  const ctx: LaikaPageContext = {
    pathname: pathname.slice(0, 200),
    page: matched.page,
    label: PAGE_LABELS[matched.page],
  };
  if (matched.entity) ctx.entity = matched.entity;

  const hints: string[] = [];
  if (matched.view_hint) hints.push(matched.view_hint);

  if (matched.page === 'planning' || matched.page === 'unavailabilities' || matched.page === 'association_planning') {
    try {
      const kind = readPlanningViewKind();
      if (kind === 'capacity') {
        hints.push('mode capacity');
        hints.push('vue capacité / bandes');
      } else {
        hints.push(`mode ${kind}`);
      }
    } catch {
      /* SSR / tests */
    }
  }

  if (search && (matched.page === 'planning' || matched.page === 'association_planning')) {
    const params = new URLSearchParams(search);
    const date = params.get('date') || params.get('day');
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      hints.push(`jour ${date}`);
    }
    const view = params.get('view');
    if (view && ['day', 'week', 'month', 'agenda', 'capacity'].includes(view)) {
      if (view === 'capacity') {
        hints.push('grille capacity');
        hints.push('vue capacité / bandes');
      } else {
        hints.push(`grille ${view}`);
      }
    }
    const status = params.get('status') || params.get('filtre');
    if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
      hints.push(`filtre ${status}`);
    }
    const mode = params.get('mode') || params.get('vue');
    if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
      hints.push(`mode query ${mode}`);
    }
    const from = params.get('from') || params.get('start');
    const to = params.get('to') || params.get('end');
    if (from && /^[0-9-]{4,12}$/.test(from)) {
      hints.push(`du ${from}`);
    }
    if (to && /^[0-9-]{4,12}$/.test(to)) {
      hints.push(`au ${to}`);
    }
    const service = params.get('service') || params.get('service_id');
    if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
      hints.push('service ciblé');
    }
    const employee = params.get('employee') || params.get('pro') || params.get('staff');
    if (employee && /^[a-z0-9_-]{1,64}$/i.test(employee)) {
      hints.push('collaborateur ciblé');
    }
    const client = params.get('client') || params.get('client_id');
    if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
      hints.push('client ciblé');
    }
    const q = params.get('q') || params.get('search');
    if (q && q.length <= 40) {
      hints.push('recherche planning');
    }
    const resource = params.get('resource') || params.get('room');
    if (resource && /^[a-z0-9_-]{1,40}$/i.test(resource)) {
      hints.push(`ressource ${resource}`);
    }
  }


  if (search && matched.page === 'settings') {
    hints.push('paramètres du compte pro');
    const params = new URLSearchParams(search);
    const tab = params.get('tab') || params.get('section');
    if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
      if (/^rappels?$/i.test(tab) || /^reminders?$/i.test(tab)) {
        hints.push('rappels email encore dans Paramètres');
        hints.push('SMS → Communication → SMS (?tab=sms) : toggle + crédits');
        hints.push('pas d\'outil create_reminder — guidance seule');
      } else if (/^widget$/i.test(tab)) {
        hints.push('widget déplacé vers Communication → Widget');
        hints.push('ouvrir /app/marketing?tab=channels');
      } else if (/^paiements?$/i.test(tab) || /^stripe$/i.test(tab) || /^payments?$/i.test(tab)) {
        hints.push('onglet paiements');
        hints.push('connexion Stripe');
      } else if (/^notifications?$/i.test(tab) || /^alertes?$/i.test(tab)) {
        hints.push('onglet notifications');
        hints.push('préférences alertes');
      } else if (/^equipe$/i.test(tab) || /^team$/i.test(tab) || /^collaborateurs?$/i.test(tab)) {
        hints.push('onglet équipe');
        hints.push('collaborateurs / accès');
      } else if (/^general$/i.test(tab) || /^profil$/i.test(tab) || /^account$/i.test(tab)) {
        hints.push('onglet général');
      } else if (/^facturation$/i.test(tab) || /^billing$/i.test(tab) || /^invoices?$/i.test(tab)) {
        hints.push('onglet facturation');
      } else if (/^abonnement$/i.test(tab) || /^subscription$/i.test(tab) || /^premium$/i.test(tab)) {
        hints.push('onglet abonnement');
      } else {
        hints.push(`onglet ${tab}`);
      }
    }
    const q = params.get('q') || params.get('search');
    if (q && q.length <= 40) {
      hints.push('recherche paramètres');
    }
    const from = params.get('from') || params.get('source');
    if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
      hints.push(`depuis ${from}`);
    }
    const mode = params.get('mode') || params.get('vue');
    if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
      hints.push(`mode ${mode}`);
    }
    const status = params.get('status') || params.get('filtre');
    if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
      hints.push(`filtre ${status}`);
    }
    const sort = params.get('sort') || params.get('order');
    if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
      hints.push(`tri ${sort}`);
    }
    const panel = params.get('panel') || params.get('focus');
    if (panel && /^[a-z0-9_-]{1,40}$/i.test(panel)) {
      hints.push(`panneau ${panel}`);
    }
  }

  if (matched.page === 'quotes') {
    hints.push('page devis');
    if (search) {
      const params = new URLSearchParams(search);
      const qid = params.get('id') || params.get('quote') || params.get('quote_id');
      if (qid && /^[a-z0-9_-]{1,64}$/i.test(qid)) {
        hints.push('aperçu devis ouvert');
      }
      const tab = params.get('tab') || params.get('status') || params.get('filtre');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche devis');
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
      const amount = params.get('amount') || params.get('total');
      if (amount && /^[0-9._-]{1,20}$/.test(amount)) {
        hints.push(`montant ${amount}`);
      }
    }
  }

  if (matched.page === 'billing') {
    hints.push('factures / devis / encaissements');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        if (tab === 'quotes' || tab === 'devis') {
          hints.push('onglet devis');
        } else {
          hints.push(`onglet ${tab}`);
        }
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche facturation');
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const exportFmt = params.get('export') || params.get('format');
      if (exportFmt && /^[a-z0-9_-]{1,40}$/i.test(exportFmt)) {
        hints.push(`export ${exportFmt}`);
      }
      const year = params.get('year') || params.get('annee');
      if (year && /^[0-9]{4}$/.test(year)) {
        hints.push(`année ${year}`);
      }
    }
  }

  if (matched.page === 'messages') {
    hints.push('messagerie pro ↔ client');
    if (!matched.entity) {
      hints.push('liste conversations');
    } else {
      hints.push('thread ouvert');
    }
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('filter') || params.get('status');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche messages');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const thread = params.get('thread') || params.get('conversation') || params.get('conversation_id');
      if (thread && /^[a-z0-9_-]{1,64}$/i.test(thread)) {
        hints.push('conversation ciblée');
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const unread = params.get('unread') || params.get('nonlu');
      if (unread && /^[a-z0-9_-]{1,40}$/i.test(unread)) {
        hints.push(`non lus ${unread}`);
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
    }
  }

  if (matched.page === 'marketing') {
    hints.push('hub Communication');
    hints.push('Premium requis — upsell si Essentiel');
    hints.push('emails, SMS, widget, réseaux sociaux');
    if (search) {
      const params = new URLSearchParams(search);
      const tool = params.get('tool') || params.get('tab');
      if (tool && /^[a-z0-9_-]{1,40}$/i.test(tool)) {
        if (/^sms$/i.test(tool)) {
          hints.push('onglet SMS — rappels 24h + crédits');
        } else if (/^channels$/i.test(tool)) {
          hints.push('onglet Widget — embed site');
          const focus = params.get('focus');
          if (focus === 'widget') hints.push('focus widget réservation');
        } else if (/^(email|campaign|campagne)$/i.test(tool)) {
          hints.push(`outil ${tool} (non branché)`);
        } else {
          hints.push(`outil ${tool}`);
        }
      }
      const mode = params.get('mode') || params.get('preview');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const campaign = params.get('campaign') || params.get('campagne');
      if (campaign && /^[a-z0-9_-]{1,40}$/i.test(campaign)) {
        hints.push(`campagne ${campaign} (non branchée)`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche marketing');
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const channel = params.get('channel') || params.get('canal');
      if (channel && /^[a-z0-9_-]{1,40}$/i.test(channel)) {
        if (/^(email|sms)$/i.test(channel)) {
          hints.push(`canal ${channel} (non branché)`);
        } else {
          hints.push(`canal ${channel}`);
        }
      }
      const segment = params.get('segment') || params.get('tag');
      if (segment && /^[a-z0-9_-]{1,40}$/i.test(segment)) {
        hints.push(`segment ${segment}`);
      }
    }
  }

  if (matched.page === 'availability') {
    hints.push('page disponibilités (pas indisponibilités)');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const sid = params.get('service') || params.get('service_id');
      if (sid && /^[a-z0-9_-]{1,64}$/i.test(sid)) {
        hints.push('service sélectionné');
      }
      const week = params.get('week') || params.get('from') || params.get('date');
      if (week && /^[0-9-]{4,12}$/.test(week)) {
        hints.push(`période ${week}`);
      }
      const to = params.get('to') || params.get('end');
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche dispos');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const capacity = params.get('capacity') || params.get('cap');
      if (capacity && /^[a-z0-9_-]{1,40}$/i.test(capacity)) {
        hints.push(`capacité ${capacity}`);
      }
    }
  }

  if (matched.page === 'unavailabilities') {
    hints.push('blocages agenda (pas dispos service)');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view') || params.get('filter');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche indispos');
      }
      const reason = params.get('reason') || params.get('motif');
      if (reason && /^[a-z0-9_-]{1,40}$/i.test(reason)) {
        hints.push(`motif ${reason}`);
      }
    }
  }



  if (matched.page === 'stats') {
    hints.push('ProStatV2');
    hints.push('CA et activité');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('section') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const mode = params.get('date_mode') || params.get('mode');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode date ${mode}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const metric = params.get('metric') || params.get('kpi');
      if (metric && /^[a-z0-9_-]{1,40}$/i.test(metric)) {
        hints.push(`kpi ${metric}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche stats');
      }
      const service = params.get('service') || params.get('service_id');
      if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
        hints.push('service ciblé');
      }
      const compare = params.get('compare') || params.get('vs');
      if (compare && /^[a-z0-9_-]{1,40}$/i.test(compare)) {
        hints.push(`comparaison ${compare}`);
      }
    }
  }

  if (matched.page === 'services') {
    hints.push('prestations (prix / durée / widget)');
    if (search) {
      const params = new URLSearchParams(search);
      const sid = params.get('service') || params.get('service_id') || params.get('id');
      if (sid && /^[a-z0-9_-]{1,64}$/i.test(sid)) {
        hints.push('service sélectionné');
      }
      const action = params.get('action');
      if (action && /^[a-z0-9_-]{1,40}$/i.test(action)) {
        hints.push(`action ${action}`);
      }
      const status = params.get('status') || params.get('filtre') || params.get('visibility');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche services');
      }
      const category = params.get('category') || params.get('cat');
      if (category && /^[a-z0-9_-]{1,40}$/i.test(category)) {
        hints.push(`catégorie ${category}`);
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
      const price = params.get('price') || params.get('tarif');
      if (price && /^[a-z0-9._-]{1,20}$/i.test(price)) {
        hints.push(`tarif ${price}`);
      }
    }
  }

  if (matched.page === 'reservations') {
    hints.push('réservations (liste / filtres)');
    if (search) {
      const params = new URLSearchParams(search);
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const date = params.get('date') || params.get('day') || params.get('from');
      if (date && /^[0-9-]{4,12}$/.test(date)) {
        hints.push(`jour ${date}`);
      }
      const to = params.get('to') || params.get('end');
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche réservations');
      }
      const service = params.get('service') || params.get('service_id');
      if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
        hints.push('service ciblé');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
    }
  }



  if (matched.page.startsWith('association_')) {
    hints.push('espace association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
    }
  }

  if (matched.page === 'association_cgv') {
    hints.push('conditions / CGV club');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view') || params.get('mode');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const section = params.get('section') || params.get('part');
      if (section && /^[a-z0-9_-]{1,40}$/i.test(section)) {
        hints.push(`section ${section}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche CGV');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const action = params.get('action') || params.get('edit');
      if (action && /^[a-z0-9_-]{1,40}$/i.test(action)) {
        hints.push(`action ${action}`);
      }
      const version = params.get('version') || params.get('v');
      if (version && /^[a-z0-9._-]{1,20}$/i.test(version)) {
        hints.push(`version ${version}`);
      }
    }
  }

  if (matched.page === 'association_messaging') {
    hints.push('messages internes association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('filter') || params.get('status');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const thread = params.get('thread') || params.get('thread_id') || params.get('id');
      if (thread && /^[a-z0-9_-]{1,64}$/i.test(thread)) {
        hints.push('thread ouvert');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche messages asso');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const member = params.get('member') || params.get('member_id');
      if (member && /^[a-z0-9_-]{1,64}$/i.test(member)) {
        hints.push('membre ciblé');
      }
      const unread = params.get('unread') || params.get('nonlu');
      if (unread && /^[a-z0-9_-]{1,40}$/i.test(unread)) {
        hints.push(`non lus ${unread}`);
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
    }
  }

  if (matched.page === 'association_members' || matched.page === 'association_member') {
    hints.push('annuaire / fiche membre');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche membres');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const role = params.get('role') || params.get('type');
      if (role && /^[a-z0-9_-]{1,40}$/i.test(role)) {
        hints.push(`rôle ${role}`);
      }
      const member = params.get('member') || params.get('member_id');
      if (member && /^[a-z0-9_-]{1,64}$/i.test(member)) {
        hints.push('membre ciblé');
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
      const mode = params.get('mode') || params.get('layout');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const section = params.get('section') || params.get('group');
      if (section && /^[a-z0-9_-]{1,40}$/i.test(section)) {
        hints.push(`section ${section}`);
      }
      const active = params.get('active') || params.get('enabled');
      if (active && /^[a-z0-9_-]{1,20}$/i.test(active)) {
        hints.push(`actif ${active}`);
      }
    }
  }


  if (matched.page === 'association_dashboard') {
    hints.push('tableau de bord association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view') || params.get('section');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche association');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const year = params.get('year') || params.get('annee');
      if (year && /^[0-9]{4}$/.test(year)) {
        hints.push(`année ${year}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const event = params.get('event') || params.get('event_id');
      if (event && /^[a-z0-9_-]{1,64}$/i.test(event)) {
        hints.push('événement ciblé');
      }
    }
  }

  if (matched.page === 'association_clients') {
    hints.push('clients liés à l\'association');
    if (search) {
      const params = new URLSearchParams(search);
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche clients asso');
      }
      const status = params.get('status') || params.get('filtre') || params.get('tab');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const segment = params.get('segment') || params.get('tag');
      if (segment && /^[a-z0-9_-]{1,40}$/i.test(segment)) {
        hints.push(`segment ${segment}`);
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
    }
  }

  if (matched.page === 'association_events') {
    hints.push('événements association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const eid = params.get('event') || params.get('event_id') || params.get('id');
      if (eid && /^[a-z0-9_-]{1,64}$/i.test(eid)) {
        hints.push('événement ouvert');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('start');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      const to = params.get('to') || params.get('end');
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche événements');
      }
      const source = params.get('source') || params.get('via');
      if (source && /^[a-z0-9_-]{1,40}$/i.test(source)) {
        hints.push(`via ${source}`);
      }
    }
  }

  if (matched.page === 'association_planning') {
    hints.push('planning collectif association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const date = params.get('date') || params.get('day');
      if (date && /^[0-9-]{4,12}$/.test(date)) {
        hints.push(`jour ${date}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche planning asso');
      }
      const service = params.get('service') || params.get('service_id');
      if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
        hints.push('service ciblé');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
    }
  }

  if (matched.page === 'notes') {
    hints.push('compte-rendus / notes de séance');
    if (search) {
      const params = new URLSearchParams(search);
      const nid = params.get('note') || params.get('id') || params.get('note_id');
      if (nid && /^[a-z0-9_-]{1,64}$/i.test(nid)) {
        hints.push('note ouverte');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche notes');
      }
      const tab = params.get('tab') || params.get('type') || params.get('filtre') || params.get('status');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
    }
  }

  if (matched.page === 'subscription') {
    hints.push('offre / renouvellement');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('plan');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`offre ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const offer = params.get('offer') || params.get('sku');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`offre ${offer}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const billing = params.get('billing') || params.get('cycle');
      if (billing && /^[a-z0-9_-]{1,40}$/i.test(billing)) {
        hints.push(`cycle ${billing}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche abonnement');
      }
      const cta = params.get('cta') || params.get('action');
      if (cta && /^[a-z0-9_-]{1,40}$/i.test(cta)) {
        hints.push(`action ${cta}`);
      }
      const trial = params.get('trial') || params.get('essai');
      if (trial && /^[a-z0-9_-]{1,40}$/i.test(trial)) {
        hints.push(`essai ${trial}`);
      }
    }
  }

  if (matched.page === 'accounting') {
    hints.push('suivi des dépenses');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const exportFmt = params.get('export') || params.get('format');
      if (exportFmt && /^[a-z0-9_-]{1,40}$/i.test(exportFmt)) {
        hints.push(`export ${exportFmt}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche compta');
      }
      const year = params.get('year') || params.get('annee');
      if (year && /^[0-9]{4}$/.test(year)) {
        hints.push(`année ${year}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const month = params.get('month') || params.get('mois');
      if (month && /^[0-9-]{1,7}$/.test(month)) {
        hints.push(`mois ${month}`);
      }
    }
  }

  if (matched.page === 'laika') {
    hints.push('consentement accès données (roue)');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view') || params.get('step');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`étape ${tab}`);
      }
      const status = params.get('status') || params.get('consent');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const mode = params.get('mode') || params.get('flow');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche laika');
      }
      const via = params.get('via') || params.get('channel');
      if (via && /^[a-z0-9_-]{1,40}$/i.test(via)) {
        hints.push(`via ${via}`);
      }
      const version = params.get('version') || params.get('v');
      if (version && /^[a-z0-9._-]{1,20}$/i.test(version)) {
        hints.push(`version ${version}`);
      }
    }
  }


  if (matched.page === 'session_detail') {
    hints.push('séance groupe / collective');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('action');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`action ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const service = params.get('service') || params.get('service_id');
      if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
        hints.push('service ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche séance');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const date = params.get('date') || params.get('day');
      if (date && /^[0-9-]{4,12}$/.test(date)) {
        hints.push(`jour ${date}`);
      }
    }
  }

  if (matched.page === 'stripe_setup') {
    hints.push('connexion paiements Stripe');
    if (search) {
      const params = new URLSearchParams(search);
      const step = params.get('step') || params.get('tab');
      if (step && /^[a-z0-9_-]{1,40}$/i.test(step)) {
        hints.push(`étape ${step}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche Stripe');
      }
      const account = params.get('account') || params.get('acct');
      if (account && /^[a-z0-9_-]{1,64}$/i.test(account)) {
        hints.push('compte ciblé');
      }
      const offer = params.get('offer') || params.get('plan');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`offre ${offer}`);
      }
    }
  }

  if (matched.page === 'sub_success' || matched.page === 'sub_cancel') {
    hints.push('parcours abonnement');
    if (search) {
      const params = new URLSearchParams(search);
      const plan = params.get('plan') || params.get('tab') || params.get('offer');
      if (plan && /^[a-z0-9_-]{1,40}$/i.test(plan)) {
        hints.push(`offre ${plan}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const billing = params.get('billing') || params.get('cycle');
      if (billing && /^[a-z0-9_-]{1,40}$/i.test(billing)) {
        hints.push(`cycle ${billing}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche abonnement');
      }
      const cta = params.get('cta') || params.get('action');
      if (cta && /^[a-z0-9_-]{1,40}$/i.test(cta)) {
        hints.push(`action ${cta}`);
      }
      const sku = params.get('sku') || params.get('product');
      if (sku && /^[a-z0-9_-]{1,40}$/i.test(sku)) {
        hints.push(`sku ${sku}`);
      }
    }
  }

  if (matched.page === 'messaging_gate') {
    hints.push('upgrade pour Messages');
    if (search) {
      const params = new URLSearchParams(search);
      const from = params.get('from') || params.get('source') || params.get('tab');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const status = params.get('status') || params.get('plan');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`offre ${status}`);
      }
      const offer = params.get('offer') || params.get('sku');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`sku ${offer}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche gate');
      }
      const mode = params.get('mode') || params.get('flow');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const cta = params.get('cta') || params.get('action');
      if (cta && /^[a-z0-9_-]{1,40}$/i.test(cta)) {
        hints.push(`action ${cta}`);
      }
      const billing = params.get('billing') || params.get('cycle');
      if (billing && /^[a-z0-9_-]{1,40}$/i.test(billing)) {
        hints.push(`cycle ${billing}`);
      }
    }
  }

  if (matched.page === 'design_system') {
    hints.push('hors parcours pro métier');
    if (search) {
      const params = new URLSearchParams(search);
      const section = params.get('section') || params.get('tab') || params.get('component');
      if (section && /^[a-z0-9_-]{1,40}$/i.test(section)) {
        hints.push(`section ${section}`);
      }
      const mode = params.get('mode') || params.get('theme');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche design');
      }
      const variant = params.get('variant') || params.get('size');
      if (variant && /^[a-z0-9_-]{1,40}$/i.test(variant)) {
        hints.push(`variant ${variant}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const preview = params.get('preview') || params.get('demo');
      if (preview && /^[a-z0-9_-]{1,40}$/i.test(preview)) {
        hints.push(`aperçu ${preview}`);
      }
    }
  }

  if (matched.page === 'booking_detail') {
    hints.push('fiche réservation');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('action');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`action ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche RDV');
      }
      const service = params.get('service') || params.get('service_id');
      if (service && /^[a-z0-9_-]{1,64}$/i.test(service)) {
        hints.push('service ciblé');
      }
      const note = params.get('note') || params.get('note_id');
      if (note && /^[a-z0-9_-]{1,64}$/i.test(note)) {
        hints.push('note ouverte');
      }
    }
  }


  if (matched.page === 'invoice_detail') {
    hints.push('document facture / devis');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('action');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`action ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche facture');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
      const amount = params.get('amount') || params.get('total');
      if (amount && /^[0-9._-]{1,20}$/.test(amount)) {
        hints.push(`montant ${amount}`);
      }
    }
  }

  if (matched.page === 'animal_detail') {
    hints.push('fiche animal');
    if (matched.view_hint?.includes('follow-up') || matched.view_hint?.includes('suivi')) {
      hints.push('suivi follow-up');
    }
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('onglet');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`onglet ${tab}`);
      }
      const view = params.get('view') || params.get('section');
      if (view && /^[a-z0-9_-]{1,40}$/i.test(view)) {
        hints.push(`vue ${view}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche animal');
      }
      const note = params.get('note') || params.get('note_id');
      if (note && /^[a-z0-9_-]{1,64}$/i.test(note)) {
        hints.push('note ouverte');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
      const species = params.get('species') || params.get('espece');
      if (species && /^[a-z0-9_-]{1,40}$/i.test(species)) {
        hints.push(`espèce ${species}`);
      }
    }
  }

  if (matched.page === 'client_detail') {
    hints.push('fiche propriétaire');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('onglet');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`onglet ${tab}`);
      }
      const view = params.get('view') || params.get('section');
      if (view && /^[a-z0-9_-]{1,40}$/i.test(view)) {
        hints.push(`vue ${view}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche fiche');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
      const forfait = params.get('forfait') || params.get('forfait_id');
      if (forfait && /^[a-z0-9_-]{1,64}$/i.test(forfait)) {
        hints.push('forfait ciblé');
      }
    }
  }

  if (matched.page === 'clients') {
    hints.push('liste clients');
    if (matched.view_hint?.includes('segments')) {
      hints.push('vue segments');
    }
    if (search) {
      const params = new URLSearchParams(search);
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche clients');
      }
      const tab = params.get('tab') || params.get('status') || params.get('filtre');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const segment = params.get('segment') || params.get('tag');
      if (segment && /^[a-z0-9_-]{1,40}$/i.test(segment)) {
        hints.push(`segment ${segment}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const cid = params.get('client') || params.get('client_id');
      if (cid && /^[a-z0-9_-]{1,64}$/i.test(cid)) {
        hints.push('client ciblé');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
    }
  }

  if (matched.page === 'home') {
    hints.push('prochains RDV / raccourcis');
    if (search) {
      const params = new URLSearchParams(search);
      const date = params.get('date') || params.get('day');
      if (date && /^[0-9-]{4,12}$/.test(date)) {
        hints.push(`jour ${date}`);
      }
      const view = params.get('view') || params.get('tab');
      if (view && /^[a-z0-9_-]{1,40}$/i.test(view)) {
        hints.push(`vue ${view}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche accueil');
      }
      const widget = params.get('widget') || params.get('card');
      if (widget && /^[a-z0-9_-]{1,40}$/i.test(widget)) {
        hints.push(`carte ${widget}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
    }
  }

  if (matched.page === 'association_settings') {
    hints.push('réglages club / association');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('section');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`section ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche réglages');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const offer = params.get('offer') || params.get('plan');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`offre ${offer}`);
      }
      const save = params.get('save') || params.get('action');
      if (save && /^[a-z0-9_-]{1,40}$/i.test(save)) {
        hints.push(`action ${save}`);
      }
    }
  }

  if (matched.page === 'profile') {
    hints.push('identité pro / vitrine');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('section');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`onglet ${tab}`);
      }
      const view = params.get('view') || params.get('mode');
      if (view && /^[a-z0-9_-]{1,40}$/i.test(view)) {
        hints.push(`vue ${view}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const edit = params.get('edit') || params.get('action');
      if (edit && /^[a-z0-9_-]{1,40}$/i.test(edit)) {
        hints.push(`action ${edit}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche profil');
      }
      const preview = params.get('preview') || params.get('demo');
      if (preview && /^[a-z0-9_-]{1,40}$/i.test(preview)) {
        hints.push(`aperçu ${preview}`);
      }
      const locale = params.get('locale') || params.get('lang');
      if (locale && /^[a-z0-9_-]{1,10}$/i.test(locale)) {
        hints.push(`langue ${locale}`);
      }
    }
  }

  if (matched.page === 'startup') {
    hints.push('checklist démarrage');
    if (search) {
      const params = new URLSearchParams(search);
      const step = params.get('step') || params.get('tab') || params.get('etape');
      if (step && /^[a-z0-9_-]{1,40}$/i.test(step)) {
        hints.push(`étape ${step}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const skip = params.get('skip') || params.get('action');
      if (skip && /^[a-z0-9_-]{1,40}$/i.test(skip)) {
        hints.push(`action ${skip}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche démarrage');
      }
      const mode = params.get('mode') || params.get('flow');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const offer = params.get('offer') || params.get('plan');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`offre ${offer}`);
      }
    }
  }

  if (matched.page === 'sms') {
    hints.push('crédits / envois SMS (pas create_reminder)');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche SMS');
      }
      const source = params.get('source') || params.get('via');
      if (source && /^[a-z0-9_-]{1,40}$/i.test(source)) {
        hints.push(`via ${source}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const credits = params.get('credits') || params.get('balance');
      if (credits && /^[a-z0-9_-]{1,40}$/i.test(credits)) {
        hints.push(`crédits ${credits}`);
      }
    }
  }

  if (matched.page === 'notifications') {
    hints.push('historique alertes');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('filter') || params.get('type');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`filtre ${tab}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche notifications');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`statut ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const booking = params.get('booking') || params.get('booking_id');
      if (booking && /^[a-z0-9_-]{1,64}$/i.test(booking)) {
        hints.push('RDV ciblé');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
    }
  }

  if (matched.page === 'partners') {
    hints.push('partenaires Rintintin');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('partner') || params.get('id');
      if (tab && /^[a-z0-9_-]{1,64}$/i.test(tab)) {
        hints.push(`partenaire ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche partenaires');
      }
      const category = params.get('category') || params.get('cat');
      if (category && /^[a-z0-9_-]{1,40}$/i.test(category)) {
        hints.push(`catégorie ${category}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
      const offer = params.get('offer') || params.get('sku');
      if (offer && /^[a-z0-9_-]{1,40}$/i.test(offer)) {
        hints.push(`offre ${offer}`);
      }
      const partner = params.get('partner') || params.get('partner_id');
      if (partner && /^[a-z0-9_-]{1,64}$/i.test(partner)) {
        hints.push('partenaire ciblé');
      }
      const city = params.get('city') || params.get('ville');
      if (city && /^[a-z0-9_-]{1,40}$/i.test(city)) {
        hints.push(`ville ${city}`);
      }
      const network = params.get('network') || params.get('reseau');
      if (network && /^[a-z0-9_-]{1,40}$/i.test(network)) {
        hints.push(`réseau ${network}`);
      }
    }
  }

  if (matched.page === 'boarding') {
    hints.push('pension / hébergement');
    if (matched.view_hint?.includes('détail séjour')) {
      hints.push('fiche séjour');
    }
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`onglet ${tab}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const from = params.get('from') || params.get('start');
      const to = params.get('to') || params.get('end');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      if (to && /^[0-9-]{4,12}$/.test(to)) {
        hints.push(`au ${to}`);
      }
      const stay = params.get('stay') || params.get('stay_id');
      if (stay && /^[a-z0-9_-]{1,64}$/i.test(stay)) {
        hints.push('séjour ciblé');
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche pension');
      }
      const animal = params.get('animal') || params.get('animal_id');
      if (animal && /^[a-z0-9_-]{1,64}$/i.test(animal)) {
        hints.push('animal ciblé');
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const room = params.get('room') || params.get('box');
      if (room && /^[a-z0-9_-]{1,40}$/i.test(room)) {
        hints.push(`box ${room}`);
      }
    }
  }

  if (matched.page === 'team') {
    hints.push('gestion collaborateurs');
    if (search) {
      const params = new URLSearchParams(search);
      const tab = params.get('tab') || params.get('view');
      if (tab && /^[a-z0-9_-]{1,40}$/i.test(tab)) {
        hints.push(`vue ${tab}`);
      }
      const emp =
        params.get('employee') ||
        params.get('employee_id') ||
        params.get('member') ||
        params.get('id');
      if (emp && /^[a-z0-9_-]{1,64}$/i.test(emp)) {
        hints.push('fiche collaborateur ouverte');
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche équipe');
      }
      const from = params.get('from') || params.get('source');
      if (from && /^[a-z0-9_-]{1,40}$/i.test(from)) {
        hints.push(`depuis ${from}`);
      }
      const role = params.get('role') || params.get('role_id');
      if (role && /^[a-z0-9_-]{1,40}$/i.test(role)) {
        hints.push(`rôle ${role}`);
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
      const mode = params.get('mode') || params.get('vue');
      if (mode && /^[a-z0-9_-]{1,40}$/i.test(mode)) {
        hints.push(`mode ${mode}`);
      }
    }
  }

  if (matched.page === 'forfaits') {
    hints.push('packs / crédits clients');
    if (search) {
      const params = new URLSearchParams(search);
      const action = params.get('action');
      if (action && /^[a-z0-9_-]{1,40}$/i.test(action)) {
        hints.push(`action ${action}`);
      }
      const status = params.get('status') || params.get('filtre');
      if (status && /^[a-z0-9_-]{1,40}$/i.test(status)) {
        hints.push(`filtre ${status}`);
      }
      const fid =
        params.get('forfait') || params.get('forfait_id') || params.get('id');
      if (fid && /^[a-z0-9_-]{1,64}$/i.test(fid)) {
        hints.push('détail forfait ouvert');
      }
      const from = params.get('from') || params.get('start');
      if (from && /^[0-9-]{4,12}$/.test(from)) {
        hints.push(`du ${from}`);
      }
      const q = params.get('q') || params.get('search');
      if (q && q.length <= 40) {
        hints.push('recherche forfaits');
      }
      const source = params.get('source') || params.get('via');
      if (source && /^[a-z0-9_-]{1,40}$/i.test(source)) {
        hints.push(`via ${source}`);
      }
      const client = params.get('client') || params.get('client_id');
      if (client && /^[a-z0-9_-]{1,64}$/i.test(client)) {
        hints.push('client ciblé');
      }
      const credits = params.get('credits') || params.get('balance');
      if (credits && /^[a-z0-9_-]{1,40}$/i.test(credits)) {
        hints.push(`crédits ${credits}`);
      }
      const sort = params.get('sort') || params.get('order');
      if (sort && /^[a-z0-9_-]{1,40}$/i.test(sort)) {
        hints.push(`tri ${sort}`);
      }
    }
  }

  const snap = readLaikaPageSnapshot();
  if (
    snap?.summary &&
    (!snap.page ||
      snap.page === matched.page ||
      snap.page === 'planning' ||
      snap.page === 'association_planning' ||
      snap.page === 'availability')
  ) {
    ctx.visible_summary = String(snap.summary).slice(0, 280);
    hints.push('snapshot vue disponible');
  }

  const hashSection = (hash || '')
    .replace(/^#/, '')
    .split(/[?&]/)[0]
    .toLowerCase();
  const cgvFromHash = hashSection === 'cgv';
  const cgvFromSnap = Boolean(
    snap?.summary && /section cgv/i.test(String(snap.summary))
  );
  if (
    matched.page === 'association_cgv' ||
    (matched.page === 'profile' && (cgvFromHash || cgvFromSnap))
  ) {
    hints.push('section CGV');
    hints.push('rédaction des conditions générales de vente');
    hints.push('atelier d’articles optionnel');
    hints.push('pas un conseil juridique — aide à formuler');
  }

  if (hints.length) {
    ctx.view_hint = hints.join(' · ');
  }

  return ctx;
}
